/**
 * Toast port.
 *
 * The implementation is a rendered component, so it lives in each app's UI layer
 * and registers itself here on mount. Shared code only ever calls ToastService.
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastShowOptions {
  type?: ToastType;
  message?: string;
  duration?: number;
  /**
   * Rich notification fields. When `title` is present the host app renders a
   * structured notification (avatar, title, subtitle) instead of the flat
   * text toast. All fields are optional and backward compatible.
   */
  /** Avatar image URL, or null/omitted for the letter fallback. */
  avatar?: string | null;
  /** Display name shown as the notification title. */
  title?: string;
  /** Preview text shown beneath the title. */
  subtitle?: string;
  /** Stable identity for deterministic avatar fallback coloring. */
  avatarSeed?: string | null;
  /** Router path to navigate to when the notification is clicked. */
  actionPath?: string;
}

type ToastHandler = (options: ToastShowOptions) => void;

let handler: ToastHandler | null = null;

/** Called by the host app's toast component on mount; pass null on unmount. */
export const registerToastHandler = (next: ToastHandler | null): void => {
  handler = next;
};

export const ToastService = {
  show: (options: ToastShowOptions): void => {
    if (handler) {
      handler(options);
      return;
    }
    console.warn('[Toast] No handler registered:', options.message);
  },
};
